import { create } from 'zustand';
import { isWithinTripRange } from '../../utils/dateUtils';
import {
    sendOpenMap,
    sendCloseMap,
    sendLoadingMap,
    sendRoute,
    sendTrajeto,
    sendSelectedPoint,
    sendMapFocus,
    sendPositionsRange,
    sendClearPositionsRange,
    sendLastPosition,
    sendClearLastPosition,
    sendAppendPositionRange
} from '../../channels/mapChannel';

import {
    sendPezinhoActive,
    sendPezinhoPeriod,
    sendPezinhoVehicles,
    sendPezinhoSelectedVehicles,
    sendPezinhoSelectedPosition,
    sendResetPezinhoSearch,
    sendClearPezinhoResults,
    sendClearPezinho
} from '../../channels/pezinhoChannel';

const MAX_APPEND_AFTER_END_MINUTES = 10;

const initialPezinhoState = {
    requestId: null,
    active: false,
    startAt: null,
    endAt: null,
    vehicles: [],
    selectedVehicles: [],
    selectedPositions: [],
    loading: false,
    loaded: false
};

const initialState = {
    codigoMap: null,
    tripUidMap: null,
    modeMap: 'line',
    openMap: false,
    route: null,
    trajeto: [],
    mapFocus: null,
    selectedPoint: null,
    mapReady: false,
    tripData:  null,
    positionsRange: [],
    lastPosition: null,
    loadingMap: false,
    pezinho: {
        ...initialPezinhoState
    }
};

function formattedRoute(route) {
    return route ? String(route).replace(/\\n/g, '\n') : null;
}

export const useMapStore = create((set, get) => ({

    ...initialState,

    setOpenMap: ({
        modeMap,
        codigoMap,
        tripData = null
    }, broadcast = true) => {

        const mapFocus = {
            type: 'bounds',
            requestId: crypto.randomUUID()
        };

        const tripUidMap =
            modeMap === 'trip'
                ? tripData?.TRIP_UID ?? null
                : null;

        set({
            codigoMap,
            tripUidMap,
            modeMap,
            openMap: true,
            selectedPoint: null,
            tripData,
            mapFocus
        });

        if (broadcast) {

            sendOpenMap({
                modeMap,
                codigoMap,
                tripData
            });
        }

    },

    setLoadingMap: (loadingMap, broadcast = true) => {
        set({
            loadingMap
        });

        if(broadcast) {
            sendLoadingMap(loadingMap);
        }
    },

    setCloseMap: (broadcast = true) => {

        set(initialState);

        if(broadcast) {
            sendCloseMap();
        }

    },

    setRoute: (data, broadcast = true) => {

        set({
            route: formattedRoute(data?.route),
        });

        if(broadcast) {
            sendRoute(data);
        }

    },

    setTrajeto: (data, broadcast = true) => {

        set({
            trajeto: data?.trajeto ?? [],
        });

        if(broadcast) {
            sendTrajeto(data);
        }

    },

    setMapFocus: (mapFocus, broadcast = true) => {

        if (!mapFocus) {

            set({
                mapFocus: null
            });

            if (broadcast) {
                sendMapFocus(null);
            }

            return;
        }

        const nextMapFocus = {
            ...mapFocus,
            requestId: crypto.randomUUID()
        };

        set({
            mapFocus: nextMapFocus
        });

        if (broadcast) {
            sendMapFocus(nextMapFocus);
        }

    },

    receiveMapFocus: (mapFocus) => {

        if (!mapFocus?.requestId) {
            return;
        }

        set({
            mapFocus
        });

    },

    setSelectedPoint: (ponto, broadcast = true) => {

        set({
            selectedPoint: ponto,
        });

        if(broadcast) {
            sendSelectedPoint(ponto);
        }

    },

    setMapReady: (ready) => {
        set({
            mapReady: ready
        });
    },

    setPositionsRange: (positionsRange, broadcast = true) => {

        set({
            positionsRange
        });

        if(broadcast) {
            sendPositionsRange(positionsRange);
        }

    },

    appendPositionRange: (position, broadcast = true) => {

        set(state => {

            const {
                tripData,
                positionsRange
            } = state;

            if (
                !tripData ||
                !position?.POSITION_UID
            ) {
                return state;
            }

            // veículo diferente
            if (
                String(position.placa).trim() !==
                String(tripData.placa).trim()
            ) {
                return state;
            }

            if (
                !isWithinTripRange(
                    position.dataHora,
                    tripData.startAt,
                    tripData.endAt,
                    MAX_APPEND_AFTER_END_MINUTES
                )
            ) {
                return state;
            }

            // já existe
            if (
                positionsRange.some(
                    p => p.POSITION_UID === position.POSITION_UID
                )
            ) {
                return state;
            }

            return {
                positionsRange: [
                    ...positionsRange,
                    position
                ].sort(
                    (a, b) =>
                        new Date(a.dataHora) -
                        new Date(b.dataHora)
                )
            };

        });

        if(broadcast){
            sendAppendPositionRange(position);
        }

    },

    clearPositionsRange: (broadcast = true) => {

        set({
            positionsRange: []
        });

        if(broadcast) {
            sendClearPositionsRange();
        }

    },

    setLastPosition: (lastPosition, broadcast = true) => {

        set({
            lastPosition
        });

        if(broadcast) {
            sendLastPosition(lastPosition);
        }

    },

    clearLastPosition: (broadcast = true) => {

        set({
            lastPosition: null
        });

        if(broadcast) {
            sendClearLastPosition();
        }

    },

    resetMap: () => {
        set({
            ...initialState
        });
    },

    setPezinhoRequestId: (requestId) => {

        set(state => ({
            pezinho: {
                ...state.pezinho,
                requestId
            }
        }));

    },

    setPezinhoActive: (
        active,
        broadcast = true
    ) => {

        set(state => ({
            pezinho: {
                ...state.pezinho,
                active
            }
        }));

        if (broadcast) {
            sendPezinhoActive(active);
        }

    },
    
    setPezinhoPeriod: (
        {
            startAt,
            endAt
        },
        broadcast = true
    ) => {

        set(state => ({
            pezinho: {
                ...state.pezinho,
                startAt,
                endAt
            }
        }));

        if (broadcast) {

            sendPezinhoPeriod({
                startAt,
                endAt
            });

        }

    },

    setPezinhoVehicles: (
        vehicles,
        broadcast = true
    ) => {

        const nextVehicles =
            Array.isArray(vehicles)
                ? vehicles
                : [];

        set(state => ({
            pezinho: {
                ...state.pezinho,

                vehicles:
                    nextVehicles,

                selectedVehicles:
                    nextVehicles
                        .map(vehicle =>
                            String(
                                vehicle?.placa || ''
                            ).trim()
                        )
                        .filter(Boolean),
                loaded: true
            }
        }));

        if (broadcast) {
            sendPezinhoVehicles(nextVehicles);
        }

    },

    setPezinhoSelectedVehicles: (
        selectedVehicles,
        broadcast = true
    ) => {

        const nextSelectedVehicles =
            selectedVehicles ?? [];

        set(state => ({
            pezinho: {
                ...state.pezinho,
                selectedVehicles:
                    nextSelectedVehicles
            }
        }));

        if (broadcast) {

            sendPezinhoSelectedVehicles(nextSelectedVehicles);

        }

    },

    setPezinhoSelectedPosition: (
        position,
        broadcast = true
    ) => {

        const placa =
            String(
                position?.placa || ''
            ).trim();

        if (!placa) {
            return;
        }

        set(state => {

            const selectedPositions =
                state.pezinho.selectedPositions || [];

            const current =
                selectedPositions.find(
                    item =>
                        String(
                            item?.placa || ''
                        ).trim() === placa
                );

            // =========================
            // MESMA POSIÇÃO → REMOVE
            // =========================

            if (
                current &&
                current.POSITION_UID ===
                    position.POSITION_UID
            ) {

                return {
                    pezinho: {
                        ...state.pezinho,

                        selectedPositions:
                            selectedPositions.filter(
                                item =>
                                    String(
                                        item?.placa || ''
                                    ).trim() !== placa
                            )
                    }
                };
            }

            // =========================
            // ADICIONA / SUBSTITUI PLACA
            // =========================

            return {
                pezinho: {
                    ...state.pezinho,

                    selectedPositions: [
                        ...selectedPositions.filter(
                            item =>
                                String(
                                    item?.placa || ''
                                ).trim() !== placa
                        ),

                        position
                    ]
                }
            };

        });

        if (broadcast) {
            sendPezinhoSelectedPosition(position);
        }

    },

    setPezinhoLoading: (loading) => {

        set(state => ({
            pezinho: {
                ...state.pezinho,
                loading
            }
        }));

    },

    resetPezinhoSearch: (
        broadcast = true
    ) => {

        set(state => ({
            pezinho: {
                ...state.pezinho,

                requestId: null,
                loading: false,
                loaded: false,

                vehicles: [],
                selectedVehicles: [],
                selectedPositions: []
            }
        }));

        if (broadcast) {
            sendResetPezinhoSearch();
        }

    },

    clearPezinhoResults: (
        broadcast = true
    ) => {

        set(state => ({
            pezinho: {
                ...state.pezinho,

                vehicles: [],
                selectedVehicles: [],
                selectedPositions: []
            }
        }));

        if (broadcast) {
            sendClearPezinhoResults();
        }

    },

    clearPezinho: (
        broadcast = true
    ) => {

        set({
            pezinho: {
                ...initialPezinhoState
            }
        });

        if (broadcast) {
            sendClearPezinho();
        }

    }

}));